import apiClient from '../../../lib/axios';

export type QuickReplyType = 'text' | 'interactive';
export type QuickReplyInteractiveType = 'button' | 'list' | 'eshop';
export type QuickReplyMediaType = 'image' | 'video' | 'document';
export type MatchType = 'exact' | 'contains';

export interface EshopFunctionParamConfig {
  name: string;
  question: string;
  regex_error_message: string | null;
}

export interface QuickReplyButton {
  id: string;
  title: string;
  next_template_id?: number | null;
}

export interface QuickReplySectionRow {
  id: string;
  title: string;
  description?: string | null;
  next_template_id?: number | null;
}

export interface QuickReplySection {
  title: string;
  rows: QuickReplySectionRow[];
}

export type QuickReplySource = 'system' | 'custom';

export interface QuickReplyTemplate {
  id: number;
  tenant_id: number;
  name: string;
  command: string;
  source: QuickReplySource;
  type: QuickReplyType;
  interactive_type: QuickReplyInteractiveType | null;
  header: string | null;
  body: string;
  footer: string | null;
  media_type: QuickReplyMediaType | null;
  media_url: string | null;
  media_preview_url: string | null;
  buttons: QuickReplyButton[] | null;
  list_button_text: string | null;
  sections: QuickReplySection[] | null;
  eshop_function_key: string | null;
  eshop_function_params: EshopFunctionParamConfig[] | null;
  eshop_cancel_button_label: string | null;
  is_auto_reply: boolean;
  keywords: string[] | null;
  match_type: MatchType | null;
  priority: number;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface QuickReplyListResponse {
  data: QuickReplyTemplate[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface CreateQuickReplyPayload {
  name: string;
  command: string;
  type: QuickReplyType;
  interactive_type?: QuickReplyInteractiveType | null;
  header?: string | null;
  body: string;
  footer?: string | null;
  media_type?: string | null;
  media_url?: string | null;
  buttons?: QuickReplyButton[] | null;
  list_button_text?: string | null;
  sections?: QuickReplySection[] | null;
  eshop_function_key?: string | null;
  eshop_function_params?: EshopFunctionParamConfig[] | null;
  eshop_cancel_button_label?: string | null;
  is_auto_reply?: boolean;
  keywords?: string[] | null;
  match_type?: MatchType | null;
  priority?: number;
  is_enabled?: boolean;
}

export interface UpdateQuickReplyPayload {
  name?: string;
  command?: string;
  type?: QuickReplyType;
  interactive_type?: QuickReplyInteractiveType | null;
  header?: string | null;
  body?: string;
  footer?: string | null;
  media_type?: string | null;
  media_url?: string | null;
  buttons?: QuickReplyButton[] | null;
  list_button_text?: string | null;
  sections?: QuickReplySection[] | null;
  eshop_function_key?: string | null;
  eshop_function_params?: EshopFunctionParamConfig[] | null;
  eshop_cancel_button_label?: string | null;
  is_auto_reply?: boolean;
  keywords?: string[] | null;
  match_type?: MatchType | null;
  priority?: number;
  is_enabled?: boolean;
}

export interface MediaUploadResponse {
  path: string;
  url: string;
  mime_type: string;
  filename: string;
}

export const quickReplyApi = {
  list: (params?: { search?: string; page?: number; per_page?: number }) =>
    apiClient.get<QuickReplyListResponse>('/v1/quick-reply-templates', { params }),

  create: (data: CreateQuickReplyPayload) =>
    apiClient.post<QuickReplyTemplate>('/v1/quick-reply-templates', data),

  update: (id: number, data: UpdateQuickReplyPayload) =>
    apiClient.put<QuickReplyTemplate>(`/v1/quick-reply-templates/${id}`, data),

  remove: (id: number) =>
    apiClient.delete(`/v1/quick-reply-templates/${id}`),

  bulkDelete: (ids: number[]) =>
    apiClient.post<{ deleted: number }>('/v1/quick-reply-templates/bulk-delete', { ids }),

  uploadMedia: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<MediaUploadResponse>('/v1/quick-reply-templates/upload-media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};
